import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import {
  SidePanel,
  SidePanelType,
  responseErrorsResolver,
  ResponseError,
  ButtonPrimary,
  ButtonSecondary,
  PermissionDenied,
  Popup,
  PermissionEnum,
  usePrevious,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  useCreateCustomersExternalAccountMutation,
  useLazyGetCustomersPspPartnersQuery,
  useLazyGetCustomersExternalAccountsListQuery,
  useLazyGetCustomersPspIntegrationsQuery,
  useCreateCustomersExternalAccountWithIntegrationMutation,
  useLazyGetPspIntegrationsOrganizationsAccountsQuery,
  useBindExternalAccountToClientAccountMutation,
} from '../store/services/customers';
import {
  Balance,
  ExternalAccount,
  ExternalAccountFormValues,
  ExternalAccountToClientAccountFormValues,
  ExternalAccountWithIntegrationFormValues,
  ExternalOrganizationAccount,
  ExternalPspPartner,
  OrganizationType,
  PspIntegration,
} from '../types';
import ExternalAccountCreateForm from '../../../components/partial/external/create';
import { useLazyGetIndividualAccountBalanceQuery } from '../../individual/store/services/individual';
import { setOpenPayNotificationVisible } from '../store/slices/external-accounts';
import LinkCustomerForm from './link-customer';

import ROUTES from '../../../routes';

const defaultFormValues = {
  pspPartnerId: '',
  accountId: '',
  iban: '',
  paymentType: '',
  currencyId: [],
  status: 'enabled',
};

const CreateCustomersExternalAccount = ({
  type,
}: {
  type: OrganizationType;
}) => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [error, setErrorValue] = useState('');
  const [selectedPartner, setSelectedPartner] = useState<ExternalPspPartner>();
  const [balances, setBalances] = useState<Balance[]>([]);
  const [isOpenPayd, setIsOpenPayd] = useState(false);
  const [isPrimeVault, setIsPrimeVault] = useState(false);
  const [isKeel, setIsKeel] = useState(false);
  const [openPaydKeelIntegration, setOpenPaydKeelIntegration] = useState<
    PspIntegration | undefined
  >(undefined);
  const [integrationType, setIntegrationType] = useState<
    'with-integration' | 'without-integration'
  >('without-integration');
  const [creationType, setCreationType] = useState<'create-new' | 'bind'>(
    'create-new',
  );
  const [bindOrganizationPopupVisible, setBindOrganizationPopupVisible] =
    useState(false);
  const [
    organizationPspExternalAccountsList,
    setOrganizationPspExternalAccountsList,
  ] = useState<ExternalOrganizationAccount[]>([]);
  const [
    selectedExternalOrganizationAccount,
    setSelectedExternalOrganizationAccount,
  ] = useState<ExternalOrganizationAccount | undefined>(undefined);
  const [bindIsNotAvailalbe, setBindIsNotAvailable] = useState(false);
  const [primeVaultExternalId, setPrimeVaultExternalId] = useState('');

  const { search } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { customerId } = useParams();

  const {
    customersDictionaries: { paymentTypes, activePermissions, casp },
    customersExternalAccounts: { pspPartners, pspIntegrations },
    individualCustomers: {
      info: {
        general: { baseInformation: individualBaseInformation },
      },
    },
    corporateCustomers: {
      info: {
        general: { baseInformation: corporateBaseInformation },
      },
    },
  } = useAppSelector((state) => state);

  const baseInformation =
    type === OrganizationType.INDIVIDUAL
      ? individualBaseInformation
      : corporateBaseInformation;

  const [createExternalAccount, { isLoading, isSuccess }] =
    useCreateCustomersExternalAccountMutation();
  const [
    createExternalAccountWithIntegration,
    { isLoading: createInProgress, isSuccess: createIsSuccess },
  ] = useCreateCustomersExternalAccountWithIntegrationMutation();

  const [getPspPartners] = useLazyGetCustomersPspPartnersQuery();
  const [getExternalAccountsList] =
    useLazyGetCustomersExternalAccountsListQuery();
  const [
    getBalances,
    { isLoading: balancesIsLoading, isFetching: balancesIsFetching },
  ] = useLazyGetIndividualAccountBalanceQuery();
  const [getPspIntegrations] = useLazyGetCustomersPspIntegrationsQuery();
  const [
    getOrganizationExternalAccounts,
    {
      isLoading: externalAccountsIsLoading,
      isFetching: externalAccountsIsFetching,
    },
  ] = useLazyGetPspIntegrationsOrganizationsAccountsQuery();
  const [
    bindExternalAccountToClientAccount,
    { isLoading: bindExternalInProgress, isSuccess: bindExternalIsSuccess },
  ] = useBindExternalAccountToClientAccountMutation();

  const {
    reset,
    control,
    formState: { errors, isDirty },
    setError,
    handleSubmit,
    setValue,
    watch,
    getValues,
  } = useForm<ExternalAccountFormValues>({
    defaultValues: defaultFormValues,
  });

  const pspPartnerId = watch('pspPartnerId');
  const paymentType = watch('paymentType');
  const currencyId = watch('currencyId');
  const accountId = watch('accountId');
  const caspId = watch('caspId');

  const prevIntegrationType = usePrevious(integrationType);
  const prevCaspId = usePrevious(caspId);
  const prevPaymentType = usePrevious(paymentType);

  const getAccountsBalancesAction = useCallback(async (id: string) => {
    const response = await getBalances(id);

    if (response) {
      setBalances(
        response?.data?.data?.edges?.map((item) => item.node) as Balance[],
      );
    }
  }, []);

  const onSubmit = handleSubmit(async (values) => {
    setErrorValue('');

    const body: ExternalAccountFormValues = {
      pspPartnerId: values.pspPartnerId,
      accountId: values.accountId,
      iban: values.iban,
      paymentType: values.paymentType,
      currencyId: values.currencyId,
      active: Boolean(values.status === 'enabled'),
      currenciesIds: values.currencyId,
    };

    if (values?.caspId) {
      body.caspId = values.caspId;
    }

    const response = (await createExternalAccount(body)) as {
      error: ResponseError | { data: { message: string } };
      data?: { data?: ExternalAccount };
    };

    if (response) {
      if (response?.error) {
        responseErrorsResolver({
          errors: response.error as ResponseError,
          setError,
          setGlobalError: setErrorValue,
        });
      } else {
        getExternalAccountsList(
          `?take=20&organizationId=${customerId}${
            search ? search.replace('?', '&') : ''
          }`,
        );
      }
    }
  });

  const onSubmitWithIntegration = handleSubmit(async (values) => {
    setErrorValue('');

    if (creationType === 'create-new') {
      const body: ExternalAccountWithIntegrationFormValues = {
        accountsIds: [values.accountId],
        integrationId: selectedPartner?.pspIntegrationId as string,
        currenciesIds: values.currencyId,
      };

      if (values.caspId) {
        body.caspId = values.caspId;
      }

      const response = (await createExternalAccountWithIntegration(body)) as {
        error: ResponseError | { data: { message: string } };
        data?: { data?: ExternalAccount };
      };

      if (response?.error) {
        responseErrorsResolver({
          errors: response.error as ResponseError,
          setError,
          setGlobalError: setErrorValue,
        });
      }
    } else if (creationType === 'bind') {
      const formValues = getValues();

      if (!formValues.caspId && isPrimeVault) {
        setError('caspId', {
          type: 'custom',
          message: 'CASP is required',
        });
      } else {
        const body: ExternalAccountToClientAccountFormValues = {
          pspPartnerId: formValues.pspPartnerId,
          accountId: formValues.accountId,
          externalId:
            isOpenPayd || isKeel
              ? (selectedExternalOrganizationAccount?.externalId as string)
              : primeVaultExternalId,
        };

        if (formValues.caspId) {
          body.caspId = formValues.caspId;
        }

        const response = await bindExternalAccountToClientAccount(body);

        if (response?.error) {
          responseErrorsResolver({
            errors: response.error as ResponseError,
            setError,
            setGlobalError: setErrorValue,
          });
        }
      }
    }
  });

  const getOrganizationExternalAccountsAction = useCallback(
    async (pspIntegrationId: string, organizationId: string) => {
      const response = await getOrganizationExternalAccounts(
        `?pspIntegrationId=${pspIntegrationId}&organizationId=${organizationId}`,
      );

      if (response?.data?.data) {
        setOrganizationPspExternalAccountsList(response?.data?.data);
      }
    },
    [],
  );

  const handleClose = () => {
    setPanelIsOpen(false);

    reset(defaultFormValues);
    setOrganizationPspExternalAccountsList([]);
    setSelectedExternalOrganizationAccount(undefined);
    setPrimeVaultExternalId('');

    let link = '';

    if (type === OrganizationType.INDIVIDUAL) {
      link = generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_EXTERNAL_ACCOUNTS_LIST, {
        customerId: customerId || '',
      });
    } else {
      link = generatePath(ROUTES.CUSTOMERS_CORPORATE_EXTERNAL_ACCOUNTS_LIST, {
        customerId: customerId || '',
      });
    }

    setTimeout(() => {
      navigate(`${link}${search || ''}`);
    }, 100);
  };

  useEffect(() => {
    if (pspPartnerId && pspPartners && pspIntegrations) {
      const currentPspPartner = pspPartners?.find(
        (item) => item.id === pspPartnerId,
      );

      if (currentPspPartner) {
        const currentPspIntegration = pspIntegrations?.find(
          (item) => item.id === currentPspPartner?.pspIntegrationId,
        );

        if (currentPspIntegration) {
          if (currentPspIntegration.name === 'OPEN_PAYD') {
            setIsOpenPayd(true);
            setOpenPaydKeelIntegration(currentPspIntegration);
            setPrimeVaultExternalId('');
            setIsPrimeVault(false);
            setIsKeel(false);
          } else if (currentPspIntegration.name === 'PRIME_VAULT_WALLET') {
            setIsOpenPayd(false);
            setOpenPaydKeelIntegration(undefined);
            setIsPrimeVault(true);
            setIsKeel(false);
          } else if (currentPspIntegration.name === 'Keel') {
            setIsOpenPayd(false);
            setOpenPaydKeelIntegration(currentPspIntegration);
            setPrimeVaultExternalId('');
            setIsPrimeVault(false);
            setIsKeel(true);
          } else {
            setIsOpenPayd(false);
            setOpenPaydKeelIntegration(undefined);
            setIsPrimeVault(false);
            setPrimeVaultExternalId('');
          }
        }
      }
    }
  }, [pspPartnerId, pspIntegrations, pspPartners]);

  useEffect(() => {
    if (!pspIntegrations) {
      getPspIntegrations();
    }
  }, [pspIntegrations]);

  useEffect(() => {
    if (accountId) {
      getAccountsBalancesAction(accountId);
    }
  }, [accountId]);

  useEffect(() => {
    if (
      (((!isPrimeVault && !isOpenPayd && !isKeel) ||
        (isPrimeVault && integrationType === 'without-integration') ||
        (isOpenPayd && integrationType === 'without-integration') ||
        (isKeel && integrationType === 'without-integration')) &&
        paymentType !== prevPaymentType) ||
      (isPrimeVault &&
        integrationType === 'with-integration' &&
        caspId !== prevCaspId)
    ) {
      setValue('currencyId', []);
    }
  }, [
    isPrimeVault,
    isOpenPayd,
    isKeel,
    integrationType,
    prevCaspId,
    prevPaymentType,
    caspId,
    paymentType,
  ]);

  useEffect(() => {
    if (
      integrationType &&
      prevIntegrationType &&
      integrationType === 'without-integration' &&
      prevIntegrationType === 'with-integration'
    ) {
      setSelectedExternalOrganizationAccount(undefined);
    }
  }, [prevIntegrationType, integrationType]);

  useEffect(() => {
    if (
      accountId &&
      openPaydKeelIntegration &&
      customerId &&
      integrationType === 'with-integration' &&
      creationType === 'bind' &&
      baseInformation &&
      baseInformation?.pspIntegrationOrganization
    ) {
      getOrganizationExternalAccountsAction(
        openPaydKeelIntegration.id,
        customerId,
      );
    }
  }, [
    accountId,
    integrationType,
    creationType,
    customerId,
    openPaydKeelIntegration,
    baseInformation,
  ]);

  useEffect(() => {
    if (isSuccess || createIsSuccess || bindExternalIsSuccess) {
      handleClose();

      if (createIsSuccess || bindExternalIsSuccess) {
        dispatch(setOpenPayNotificationVisible(true));

        setTimeout(() => {
          dispatch(setOpenPayNotificationVisible(false));
        }, 10000);
      }
    }
  }, [isSuccess, createIsSuccess, bindExternalIsSuccess]);

  useEffect(() => {
    setPanelIsOpen(true);

    if (!pspPartners) {
      getPspPartners();
    }
  }, []);

  let content = <div />;

  if (activePermissions) {
    if (
      activePermissions.includes(
        PermissionEnum.PSP_INTEGRATION_ACCOUNT_ADD_ACTION,
      )
    ) {
      content = (
        <ExternalAccountCreateForm
          control={control}
          errors={errors}
          pspPartnerId={pspPartnerId}
          paymentTypes={paymentTypes}
          paymentType={paymentType}
          setSelectedPartner={setSelectedPartner}
          pspPartners={pspPartners}
          selectedPartner={selectedPartner}
          setValue={setValue}
          currencyId={currencyId}
          accounts={baseInformation?.accounts}
          balances={balances}
          casp={casp?.map((item) => ({ name: item.name, value: item.id }))}
          accountId={accountId}
          isOpenPayd={isOpenPayd}
          isKeel={isKeel}
          integrationType={integrationType}
          setIntegrationType={setIntegrationType}
          openPaydIntegration={openPaydKeelIntegration}
          balancesIsLoading={balancesIsLoading || balancesIsFetching}
          creationType={creationType}
          setCreationType={setCreationType}
          activePermissions={activePermissions}
          setBindOrganizationPopupVisible={setBindOrganizationPopupVisible}
          pspIntegrationOrganization={
            baseInformation?.pspIntegrationOrganization
          }
          organizationPspExternalAccountsList={
            organizationPspExternalAccountsList
          }
          externalAccountsIsLoading={externalAccountsIsLoading}
          externalAccountsIsFetching={externalAccountsIsFetching}
          selectedExternalOrganizationAccount={
            selectedExternalOrganizationAccount
          }
          setSelectedExternalOrganizationAccount={
            setSelectedExternalOrganizationAccount
          }
          bindIsNotAvailalbe={bindIsNotAvailalbe}
          setBindIsNotAvailable={setBindIsNotAvailable}
          primeVaultExternalId={primeVaultExternalId}
          setPrimeVaultExternalId={setPrimeVaultExternalId}
          isPrimeVault={isPrimeVault}
          caspId={caspId}
          caspValues={casp}
          setIsPrimeVault={setIsPrimeVault}
          setIsOpenPayd={setIsOpenPayd}
          setIsKeel={setIsKeel}
          setPanelIsOpen={setPanelIsOpen}
        />
      );
    } else {
      content = <PermissionDenied />;
    }
  }

  return (
    <>
      <SidePanel
        isOpen={panelIsOpen}
        type={SidePanelType.Accounts}
        onClose={() => {
          if (isDirty) {
            setDirtyPopupVisible(true);
          } else {
            handleClose();
          }
        }}
        title="Add External Account"
        errorSidepanel={error}
        sidepanelExpandedKey="customer-create-external-account"
        footer={
          <>
            <ButtonPrimary
              text={
                isLoading || createInProgress || bindExternalInProgress
                  ? 'Loading...'
                  : 'Add account'
              }
              disabled={
                !isDirty ||
                isLoading ||
                createInProgress ||
                bindExternalInProgress ||
                (creationType === 'bind' &&
                  !activePermissions?.includes(
                    PermissionEnum.PSP_INTEGRATION_ACCOUNT_ADD_ACTION,
                  ))
              }
              onClick={() =>
                integrationType === 'with-integration'
                  ? onSubmitWithIntegration()
                  : onSubmit()
              }
              bold
            />
            <ButtonSecondary
              text="Close"
              className="button-white"
              onClick={() => {
                if (isDirty) {
                  setDirtyPopupVisible(true);
                } else {
                  handleClose();
                }
              }}
              bold
            />
          </>
        }
        closeOnClickOutside
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes"
        description=""
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <LinkCustomerForm
        type={type}
        linkExternalCustomerPopupVisible={bindOrganizationPopupVisible}
        setLinkExternalCustomerPopupVisible={setBindOrganizationPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
        selectedPartner={selectedPartner?.id}
        selectedPspIntegrationId={selectedPartner?.pspIntegrationId}
        setExternalPanelIsOpen={setPanelIsOpen}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default CreateCustomersExternalAccount;
